import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Crown, Lightning, CheckCircle, Star, ArrowLeft } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import EmailAuthModal from "../components/EmailAuthModal";
import FoundingSpotsCounter from "../components/FoundingSpotsCounter";
import { usePublicStats } from "../components/DynamicGrantCount";

const buildTiers = (scoutCount) => [
  {
    id: "starter",
    name: "Founding Starter",
    regularPrice: 19.99,
    foundingPrice: 14.99,
    promoCode: "FOUNDINGSTARTER",
    badge: "Most popular for small nonprofits",
    features: [
      "3 Flash Drafts per month",
      "Unlimited grant applications",
      "Application Packet for third-party portals",
      "Bookmarks, calendar reminders & email digests",
      "Founding Member status — locked in forever",
    ],
  },
  {
    id: "pro",
    name: "Founding Premium",
    regularPrice: 49.99,
    foundingPrice: 39.99,
    promoCode: "FOUNDINGPREMIUM",
    badge: "Maximum impact",
    featured: true,
    features: [
      "Unlimited Flash Drafts",
      `Corporate Scout (${scoutCount} verified SC funders)`,
      "Sponsorship Proposal Generator",
      "Post-Grant Manager + Impact Dashboard",
      "Cross-Nonprofit Collaboration",
      "Founding Member status — locked in forever",
    ],
  },
];

const FoundingMember = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null); // tier id when starting checkout
  const [showAuth, setShowAuth] = useState(false);
  const autoFiredRef = useRef(false);
  const publicStats = usePublicStats();
  const TIERS = useMemo(
    () => buildTiers(publicStats?.corporate_scouts || 30),
    [publicStats?.corporate_scouts]
  );

  const startCheckout = useCallback(async (tier) => {
    setLoading(tier.id);
    try {
      const res = await axios.post(`${API}/subscriptions/subscribe`, {
        tier: tier.id,
        promo_code: tier.promoCode,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start checkout. Try again or contact us.";
      alert(msg);
      setLoading(null);
    }
  }, []);

  const handleClaim = async (tier) => {
    if (!user) {
      // Store intent then open sign-in modal right here — no bounce to landing
      try { localStorage.setItem("founding_pending_tier", tier.id); } catch { /* ignore */ }
      setShowAuth(true);
      return;
    }
    await startCheckout(tier);
  };

  // After login (Google OAuth or Email), come back here and auto-fire the
  // checkout with the previously selected tier. EmailAuthModal navigates to
  // /founding?claim=1 when founding_pending_tier is set.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (!user) return;
    let pendingTierId = null;
    try { pendingTierId = localStorage.getItem("founding_pending_tier"); } catch { /* ignore */ }
    if (!pendingTierId) return;
    const tier = TIERS.find((t) => t.id === pendingTierId);
    if (!tier) {
      try { localStorage.removeItem("founding_pending_tier"); } catch { /* ignore */ }
      return;
    }
    autoFiredRef.current = true;
    try { localStorage.removeItem("founding_pending_tier"); } catch { /* ignore */ }
    startCheckout(tier);
  }, [user, startCheckout, searchParams, TIERS]);

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="founding-member-page">
      {/* Header */}
      <header className="border-b border-zinc-800/60 bg-zinc-950/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="header-home-link">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-400" />
            <span className="font-bold text-lg tracking-tight">FlashGrants</span>
          </Link>
          <button
            type="button"
            onClick={() => navigate(user ? "/pricing" : "/")}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
            data-testid="founding-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {user ? "Back to Pricing" : "Back"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-6" data-testid="founding-eyebrow">
          <Star weight="fill" className="w-3.5 h-3.5" />
          Founding Member · Invitation Only
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5" data-testid="founding-title">
          Locked in. <span className="text-emerald-400">Forever.</span>
        </h1>
        <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed" data-testid="founding-subtitle">
          You helped shape FlashGrants in its earliest weeks. This is our way of saying thank you — a permanently
          discounted rate, no expiration, no catch. The price you lock in today is the price you keep.
        </p>
        <p className="mt-4 text-sm text-zinc-500" data-testid="founding-disclaimer">
          One-click checkout. Cancel anytime. Founding rate stays with your account as long as your subscription is active.
        </p>
      </section>

      {/* Tiers */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <FoundingSpotsCounter variant="inline" />
        <div className="grid sm:grid-cols-2 gap-6">
          {TIERS.map((tier) => (
            <Card
              key={tier.id}
              className={`relative overflow-hidden border-2 transition-all ${
                tier.featured
                  ? "bg-gradient-to-b from-emerald-500/10 to-zinc-950 border-emerald-500/50"
                  : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"
              }`}
              data-testid={`founding-tier-${tier.id}`}
            >
              {tier.featured && (
                <div className="absolute top-0 right-0 px-3 py-1 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-widest rounded-bl-md" data-testid={`founding-featured-${tier.id}`}>
                  Best Value
                </div>
              )}
              <CardContent className="p-7">
                <div className="flex items-center gap-2 mb-1.5">
                  <Crown
                    weight="fill"
                    className={`w-5 h-5 ${tier.featured ? "text-emerald-400" : "text-zinc-500"}`}
                  />
                  <span className="text-xs uppercase tracking-widest text-zinc-500">{tier.badge}</span>
                </div>
                <h2 className="text-2xl font-bold tracking-tight mb-4" data-testid={`founding-name-${tier.id}`}>
                  {tier.name}
                </h2>

                <div className="mb-5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-emerald-400" data-testid={`founding-price-${tier.id}`}>
                      ${tier.foundingPrice}
                    </span>
                    <span className="text-sm text-zinc-500">/month</span>
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-sm line-through text-zinc-600" data-testid={`founding-regular-price-${tier.id}`}>
                      ${tier.regularPrice}/mo regular
                    </span>
                    <span className="text-xs text-emerald-300 font-semibold">
                      Save ${(tier.regularPrice - tier.foundingPrice).toFixed(2)}/mo · forever
                    </span>
                  </div>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle
                        weight="fill"
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.featured ? "text-emerald-400" : "text-emerald-500/70"}`}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => handleClaim(tier)}
                  disabled={loading === tier.id}
                  className={`w-full py-3 rounded-sm font-semibold text-sm uppercase tracking-widest transition-all ${
                    tier.featured
                      ? "bg-emerald-500 text-black hover:bg-emerald-400 disabled:bg-emerald-500/40"
                      : "bg-zinc-100 text-black hover:bg-white disabled:bg-zinc-400"
                  }`}
                  data-testid={`founding-claim-${tier.id}`}
                >
                  {loading === tier.id ? "Loading checkout…" : `Claim ${tier.name} → $${tier.foundingPrice}/mo`}
                </button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust footer */}
        <div className="mt-10 text-center text-xs text-zinc-500" data-testid="founding-trust">
          <p>
            Secure checkout powered by Stripe · cancel anytime from your profile · no hidden fees.
          </p>
          <p className="mt-2">
            Questions? Email{" "}
            <a href="mailto:hello@flash-grants.com" className="text-emerald-400 hover:underline">
              hello@flash-grants.com
            </a>
          </p>
        </div>
      </section>
      {showAuth && <EmailAuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default FoundingMember;
